import React from 'react';
import {
  Sparkles,
  Rocket,
  ShieldOff,
  EyeOff,
  LineChart,
  BadgeDollarSign,
  MessageSquareText,
  Search,
  Inbox,
  Wrench,
  Building2,
  ArrowUpRight,
  ChevronDown,
} from 'lucide-react';

const HeadcoreMark = () => (
  <div className="inline-flex items-center gap-3">
    <span className="inline-flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-lg bg-[#1b2a55] text-white font-bold text-lg md:text-xl tracking-tight shadow-md">
      HC
    </span>
    <span className="relative inline-block">
      <span className="text-2xl md:text-3xl lg:text-4xl font-black tracking-tight text-[#1b2a55]">
        HEADCORE
      </span>
      <span
        aria-hidden="true"
        className="absolute left-0 -bottom-1 h-1 md:h-1.5 w-full bg-[#f5b400] rounded-full"
      />
    </span>
  </div>
);

const supplierSteps = [
  {
    icon: Rocket,
    title: 'Join free in three minutes',
    blurb:
      "Give us your company registration and website address — the system builds your profile automatically. No subscription. Ever.",
  },
  {
    icon: Inbox,
    title: 'Work appears in your field',
    blurb:
      'Enquiries matching your trade, your equipment and your area arrive automatically. You see nothing that isn\u2019t relevant to you.',
  },
  {
    icon: BadgeDollarSign,
    title: 'Respond only when you want the job',
    blurb:
      'One token per response, with token bundles available as part of the platform. No commission, no middleman. If you win the work, 100% of it is yours.',
  },
];

const buyerSteps = [
  {
    icon: MessageSquareText,
    title: 'Describe the requirement in plain language',
    blurb:
      "No forms, no technical codes. Type the problem the way you'd explain it to a colleague.",
  },
  {
    icon: Search,
    title: 'The platform finds the right specialists',
    blurb:
      'AI classifies your request and routes it only to suppliers who genuinely do that work, in your area. Multi-scope jobs are split automatically.',
  },
  {
    icon: Sparkles,
    title: 'Responses come to you',
    blurb:
      'Usually within a day or two, from suppliers who have chosen to quote because they want the work.',
  },
];

const differentiators = [
  {
    icon: ShieldOff,
    title: 'No rankings, no gaming',
    blurb: 'A specialist beats a big name when the specialism fits.',
  },
  {
    icon: EyeOff,
    title: 'Complete privacy',
    blurb:
      'Nobody sees your prices, and you never see who else is competing. Buyers can post anonymously.',
  },
  {
    icon: LineChart,
    title: 'Market intelligence',
    blurb:
      'See where demand is flowing in your sector — which industries are growing, which regions are underserved.',
  },
  {
    icon: BadgeDollarSign,
    title: 'Free to join, free to stay',
    blurb:
      'Posting requests is free. Always. No subscription and no commission on work you award. Suppliers only pay when they see a job worth winning.',
  },
];

const HeadcorePage = () => {
  const scrollToDetail = () => {
    document.getElementById('headcore-detail')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Hero */}
      <section
        id="home"
        className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-[#1a1a1a] via-[#252525] to-[#2d2d2d]"
      >
        <div className="absolute inset-0 overflow-hidden opacity-25 pointer-events-none">
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hc-hero-grid" width="48" height="48" patternUnits="userSpaceOnUse">
                <path d="M 48 0 L 0 0 0 48" fill="none" stroke="rgba(62,181,160,0.18)" strokeWidth="0.6" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hc-hero-grid)" />
          </svg>
          <svg
            viewBox="0 0 600 600"
            className="absolute right-[-6%] top-1/2 -translate-y-1/2 w-[520px] h-[520px] hidden lg:block opacity-70"
            aria-hidden="true"
          >
            <circle cx="350" cy="300" r="220" fill="none" stroke="rgba(62,181,160,0.35)" strokeWidth="1.5" />
            <circle cx="350" cy="300" r="160" fill="none" stroke="rgba(62,181,160,0.3)" strokeWidth="1.2" />
            <circle cx="350" cy="300" r="100" fill="none" stroke="rgba(62,181,160,0.28)" strokeWidth="1" />
            <circle cx="350" cy="300" r="6" fill="#3eb5a0" />
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 pt-32 md:pt-40 w-full">
          <div className="max-w-5xl">
            <div className="animate-hero-rise mb-5 md:mb-6" style={{ animationDelay: '0ms' }}>
              <p className="inline-block text-xs md:text-sm uppercase tracking-[0.3em] text-[#3eb5a0] font-medium border border-[#3eb5a0]/50 rounded-full px-4 py-1.5">
                African Partnership · Reshadi &times; Headcore
              </p>
            </div>

            <div className="animate-hero-rise mb-6 md:mb-8" style={{ animationDelay: '80ms' }}>
              <div className="inline-block bg-white rounded-2xl px-5 md:px-6 py-3.5 md:py-4 shadow-lg">
                <HeadcoreMark />
              </div>
            </div>

            <p
              className="animate-hero-rise text-xs uppercase tracking-[0.3em] text-[#f5b400] font-semibold mb-4 md:mb-5"
              style={{ animationDelay: '160ms' }}
            >
              Industrial Matching Platform
            </p>

            <h1
              className="animate-hero-rise text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6 md:mb-8 max-w-5xl"
              style={{ animationDelay: '240ms' }}
            >
              AI matching that connects
              <br />
              <span className="text-[#3eb5a0]">buyers and suppliers</span>
              <br />
              of technical work.
            </h1>

            <p
              className="animate-hero-rise text-lg md:text-xl lg:text-2xl text-white/80 font-light tracking-wide leading-snug max-w-3xl mb-8 md:mb-10"
              style={{ animationDelay: '320ms' }}
            >
              An AI matching platform that connects industrial suppliers to real
              enquiries from buyers who need exactly what they do — and helps buyers
              describe a job once, and get answers in 48 hours.
            </p>

            <div
              className="animate-hero-rise flex flex-col sm:flex-row gap-4"
              style={{ animationDelay: '400ms' }}
            >
              <a
                href="https://www.headcore.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="headcore-visit-link"
                className="group inline-flex items-center justify-center gap-3 px-7 md:px-9 py-3.5 md:py-4 bg-[#f5b400] text-[#1b2a55] font-semibold rounded-full shadow-lg hover:bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#f5b400] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
              >
                <span>Visit www.headcore.com</span>
                <ArrowUpRight size={18} strokeWidth={2.4} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>

              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="headcore-engage-btn"
                className="group inline-flex items-center justify-center gap-3 px-7 md:px-9 py-3.5 md:py-4 bg-transparent border-2 border-[#3eb5a0] text-[#3eb5a0] font-semibold rounded-full hover:bg-[#3eb5a0] hover:text-black transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#3eb5a0] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
              >
                <span>Engage Reshadi</span>
                <span aria-hidden="true" className="text-lg transition-transform duration-300 group-hover:translate-x-1">
                  &rarr;
                </span>
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={scrollToDetail}
          aria-label="Scroll to how it works"
          className="absolute bottom-6 md:bottom-8 left-6 md:left-12 w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-[#3eb5a0] bg-transparent text-[#3eb5a0] flex items-center justify-center hover:bg-[#3eb5a0] hover:text-black transition-all duration-300"
        >
          <ChevronDown size={22} />
        </button>
      </section>

      {/* Detail: how it works + why it matters + partnership */}
      <section
        id="headcore-detail"
        className="relative py-16 md:py-24 lg:py-32 bg-gradient-to-b from-[#1a1a1a] via-[#141414] to-[#0d0d0d] overflow-hidden"
      >
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" aria-hidden="true">
          <svg className="w-full h-full">
            <defs>
              <pattern id="hc-detail-grid" width="56" height="56" patternUnits="userSpaceOnUse">
                <path d="M 56 0 L 0 0 0 56" fill="none" stroke="rgba(62,181,160,0.6)" strokeWidth="0.6" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hc-detail-grid)" />
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12 md:mb-16 max-w-4xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#3eb5a0] font-semibold mb-3">
              How Headcore Works
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.1]">
              Two audiences.
              <br />
              <span className="text-[#3eb5a0]">One friction-free rail.</span>
            </h2>
          </div>

          {/* Two-audience split */}
          <div className="grid lg:grid-cols-2 gap-8 md:gap-10 mb-12 md:mb-16">
            <div className="rounded-2xl bg-[#1f1f1f] border border-[#3eb5a0]/15 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6 md:mb-8">
                <div className="w-11 h-11 rounded-xl bg-[#3eb5a0]/10 border border-[#3eb5a0]/30 flex items-center justify-center flex-shrink-0">
                  <Wrench size={20} className="text-[#3eb5a0]" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#3eb5a0] font-semibold">For Suppliers</p>
                  <p className="text-lg md:text-xl font-semibold text-white">Work you can win, in your own trade</p>
                </div>
              </div>
              <ol className="space-y-5 md:space-y-6">
                {supplierSteps.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <li key={s.title} className="flex gap-4">
                      <div className="w-9 h-9 rounded-lg bg-[#3eb5a0] text-black flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {String(i + 1).padStart(2, '0')}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon size={16} className="text-[#3eb5a0]" strokeWidth={2} />
                          <h4 className="text-base md:text-lg font-semibold text-white">{s.title}</h4>
                        </div>
                        <p className="text-sm md:text-base text-white/65 font-light" style={{ lineHeight: '1.75' }}>{s.blurb}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>

            <div className="rounded-2xl bg-[#1f1f1f] border border-[#3eb5a0]/15 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6 md:mb-8">
                <div className="w-11 h-11 rounded-xl bg-[#f5b400]/10 border border-[#f5b400]/40 flex items-center justify-center flex-shrink-0">
                  <Building2 size={20} className="text-[#f5b400]" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#f5b400] font-semibold">For Buyers</p>
                  <p className="text-lg md:text-xl font-semibold text-white">Describe the job once. Answers in 48 hours.</p>
                </div>
              </div>
              <ol className="space-y-5 md:space-y-6">
                {buyerSteps.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <li key={s.title} className="flex gap-4">
                      <div className="w-9 h-9 rounded-lg bg-[#f5b400] text-[#1b2a55] flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {String(i + 1).padStart(2, '0')}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon size={16} className="text-[#f5b400]" strokeWidth={2} />
                          <h4 className="text-base md:text-lg font-semibold text-white">{s.title}</h4>
                        </div>
                        <p className="text-sm md:text-base text-white/65 font-light" style={{ lineHeight: '1.75' }}>{s.blurb}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>

          {/* Why it matters */}
          <div className="mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#3eb5a0] font-semibold mb-6 md:mb-8">
              Why it matters
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {differentiators.map((v) => {
                const Icon = v.icon;
                return (
                  <article
                    key={v.title}
                    className="group relative rounded-2xl bg-[#1f1f1f] border border-[#3eb5a0]/15 p-6 md:p-7 transition-all duration-300 hover:border-[#3eb5a0] hover:-translate-y-1 hover:shadow-2xl"
                  >
                    <div className="w-11 h-11 rounded-xl bg-[#3eb5a0]/10 border border-[#3eb5a0]/30 flex items-center justify-center flex-shrink-0 mb-4 transition-colors duration-300 group-hover:bg-[#3eb5a0]">
                      <Icon size={20} className="text-[#3eb5a0] transition-colors duration-300 group-hover:text-black" strokeWidth={2} />
                    </div>
                    <h3 className="text-lg md:text-xl font-semibold text-white mb-2">{v.title}</h3>
                    <p className="text-sm md:text-base text-white/65 font-light" style={{ lineHeight: '1.75' }}>{v.blurb}</p>
                  </article>
                );
              })}
            </div>
          </div>

          {/* Partnership */}
          <div className="rounded-2xl bg-[#3eb5a0]/[0.04] border border-[#3eb5a0]/20 p-6 md:p-8 max-w-4xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#3eb5a0] font-semibold mb-3">
              The Partnership
            </p>
            <p className="text-base md:text-lg text-white/85 font-light" style={{ lineHeight: '1.85' }}>
              <span className="font-semibold text-white">Headcore B.V.</span> is headquartered in the
              Netherlands and represented across Africa by
              {' '}
              <span className="font-semibold text-white">Reshadi Investment and Projects (Pty) Ltd</span> —
              bringing the platform to South African industrial buyers and suppliers ahead of its
              continental rollout.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeadcorePage;
